import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenAuthState } from '../store/auth/auth.state';
import { showError } from '../utility/common-logic';

@Injectable({
  providedIn: 'root',
})
export class FreshChatService {
  private openSubject = new BehaviorSubject(false);
  private freshChatScriptAttached = false;
  open$ = this.openSubject.asObservable();

  constructor(
    private ngZone: NgZone,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  get isEnabled(): boolean {
    return !!environment.settings.freshchat?.isShow;
  }

  get isOpen(): boolean {
    return this.openSubject.value;
  }

  get shouldShowLauncher(): boolean {
    if (!this.isEnabled) {
      return false;
    }
    const pages = environment.settings.freshchat?.pages;
    return pages === 'all' || pages === 'support';
  }

  open(): void {
    if (!this.isEnabled || this.isOpen) {
      return;
    }

    this.attachScriptIfNeeded();
    this.waitForWidget(() => this.openWidget());
  }

  /**
   * Keep FreshChat user identity in sync with auth store.
   * Call on init and whenever auth updates (login / logout).
   */
  updateUser(auth?: TokenAuthState | null): void {
    const fcWidget = (window as any).fcWidget;
    if (!fcWidget?.user) {
      return;
    }

    const email = auth?.user_details?.email || '';
    const firstName = auth?.user_details?.first_name || '';
    const lastName = auth?.user_details?.last_name || '';
    const organization = auth?.organization_details?.slug || '';

    fcWidget.setExternalId(email);
    fcWidget.user.setFirstName(firstName);
    fcWidget.user.setLastName(lastName);
    fcWidget.user.setEmail(email);
    fcWidget.user.setProperties({ organization });
  }

  clearUser(): void {
    this.updateUser(null);
  }

  syncWidgetVisibility(fcWidget: any): void {
    if (!fcWidget) {
      return;
    }

    if (this.shouldShowLauncher) {
      fcWidget.show();
      return;
    }

    fcWidget.hide();
  }

  /** Close messenger and reset UI state without hiding the widget (keeps login chat working). */
  resetOnLogout(): void {
    if (!this.isEnabled) {
      return;
    }

    const fcWidget = (window as any).fcWidget;
    this.setMessengerOpen(false);
    this.clearUser();
    fcWidget?.close();
    this.syncWidgetVisibility(fcWidget);
  }

  close(): void {
    const fcWidget = (window as any).fcWidget;
    if (!fcWidget) {
      this.setMessengerOpen(false);
      return;
    }

    if (typeof fcWidget.isOpen === 'function' && !fcWidget.isOpen()) {
      this.setMessengerOpen(false);
      return;
    }

    fcWidget.close();

    // Hide host after close animation so white messenger chrome cannot linger.
    window.setTimeout(() => this.setMessengerOpen(false), 320);
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  setMessengerOpen(open: boolean): void {
    this.openSubject.next(open);
    if (open) {
      document.body.classList.add('fc-messenger-open');
    } else {
      document.body.classList.remove('fc-messenger-open');
    }

    const frame = document.getElementById('fc_frame');
    if (frame) {
      frame.style.pointerEvents = open ? 'auto' : 'none';
    }
  }

  wireWidgetEvents(fcWidget: any): void {
    if (!fcWidget?.on) {
      return;
    }

    fcWidget.on('widget:opened', () => {
      this.ngZone.run(() => this.setMessengerOpen(true));
    });
    fcWidget.on('widget:closed', () => {
      this.ngZone.run(() => {
        window.setTimeout(() => this.setMessengerOpen(false), 50);
      });
    });
  }

  /**
   * Load FreshChat on first user interaction (Lighthouse). Call once from init.
   */
  registerDeferredScriptLoad(): void {
    const scriptSrc = environment.settings.freshchat?.script;
    if (!scriptSrc || this.hasScriptInDom(scriptSrc)) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const load = () => {
        this.attachScriptIfNeeded();
        ['click', 'scroll', 'keydown', 'touchstart'].forEach((e) =>
          window.removeEventListener(e, load),
        );
      };
      ['click', 'scroll', 'keydown', 'touchstart'].forEach((e) =>
        window.addEventListener(e, load, { once: true, passive: true }),
      );
    });
  }

  private openWidget(): void {
    const fcWidget = (window as any).fcWidget;
    if (!fcWidget || this.isOpen) {
      return;
    }

    this.setMessengerOpen(true);
    try {
      // Required after logout — signOut used to call fcWidget.hide().
      fcWidget.show();
      fcWidget.open();
    } catch (error) {
      this.setMessengerOpen(false);
      showError(
        `${this.translate.instant('msg.contactInfo.freshchatError')} ${error}`,
        this.toastr,
        this.translate,
        true,
      );
    }
  }

  private attachScriptIfNeeded(): void {
    const scriptSrc = environment.settings.freshchat?.script;
    if (
      !scriptSrc ||
      this.hasScriptInDom(scriptSrc) ||
      this.freshChatScriptAttached
    ) {
      return;
    }

    this.freshChatScriptAttached = true;
    const chatElement = document.createElement('script');
    chatElement.setAttribute('src', scriptSrc);
    chatElement.setAttribute('chat', 'true');
    chatElement.setAttribute('defer', '');
    document.body.appendChild(chatElement);
  }

  private hasScriptInDom(scriptSrc: string): boolean {
    return Array.from(document.querySelectorAll('script[chat="true"]')).some(
      (node) => node.getAttribute('src') === scriptSrc,
    );
  }

  private waitForWidget(callback: () => void, attempts = 0): void {
    if ((window as any).fcWidget) {
      callback();
      return;
    }

    if (attempts >= 50) {
      return;
    }

    window.setTimeout(() => this.waitForWidget(callback, attempts + 1), 100);
  }
}
